/**
 * Plain-text projection of a `.zwdoc` document (#248).
 *
 * One string for everything that reads prose — Harper, entity peek,
 * occurrences, history diff, word count — plus a map between string offsets
 * and ProseMirror positions so ranges found in the text land back on the
 * right characters.
 *
 * Shape: textblocks (paragraphs, headings, code blocks, at any depth) joined
 * by `\n\n`; `hard_break` → `\n`; images, comment anchors, spacers, rules and
 * raw blocks contribute nothing.
 */

import type { Node as PMNode } from "prosemirror-model";
import { just, nothing, type Maybe } from "../maybe";

/** A contiguous span where text offsets and doc positions advance together. */
export type PlainTextRun = {
  textFrom: number;
  docFrom: number;
  length: number;
};

/** One textblock's slice of the projection. */
export type PlainTextBlock = {
  textFrom: number;
  length: number;
  /** Heading level, or Nothing for paragraphs and code blocks. */
  headingLevel: Maybe<number>;
};

export type PlainTextProjection = {
  text: string;
  /** Sorted by both `textFrom` and `docFrom`. */
  runs: readonly PlainTextRun[];
  /** Every textblock in document order, empty ones included. */
  blocks: readonly PlainTextBlock[];
};

export const BLOCK_SEPARATOR = "\n\n";

export function projectPlainText(doc: PMNode): PlainTextProjection {
  let text = "";
  const runs: PlainTextRun[] = [];
  const blocks: PlainTextBlock[] = [];

  doc.descendants((node, pos) => {
    if (node.isTextblock === false) return true;
    if (blocks.length > 0) text += BLOCK_SEPARATOR;
    const textFrom = text.length;

    node.forEach((child, offset) => {
      const docFrom = pos + 1 + offset;
      if (child.isText) {
        const value = child.text ?? "";
        runs.push({ textFrom: text.length, docFrom, length: value.length });
        text += value;
      } else if (child.type.name === "hard_break") {
        runs.push({ textFrom: text.length, docFrom, length: 1 });
        text += "\n";
      }
    });
    blocks.push({
      textFrom,
      length: text.length - textFrom,
      headingLevel:
        node.type.name === "heading" ? just(Number(node.attrs.level)) : nothing,
    });
    return false;
  });

  return { text, runs, blocks };
}

/** Last item whose `key` start is ≤ `value`, or -1. */
function indexAtOrBefore<K extends string>(
  runs: readonly Readonly<Record<K, number>>[],
  value: number,
  key: K,
): number {
  let lo = 0;
  let hi = runs.length - 1;
  let found = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const run = runs[mid];
    if (typeof run === "undefined") break;
    if (run[key] <= value) {
      found = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return found;
}

/**
 * Doc position for a text offset. Offsets inside a block separator resolve to
 * the end of the preceding text.
 */
export function textOffsetToDocPos(
  projection: PlainTextProjection,
  offset: number,
): number {
  const { runs } = projection;
  const index = indexAtOrBefore(runs, offset, "textFrom");
  const run = runs[Math.max(index, 0)];
  if (typeof run === "undefined") return 1;
  if (index === -1) return run.docFrom;
  const within = Math.min(offset - run.textFrom, run.length);
  return run.docFrom + within;
}

/** Text offset for a doc position, or Nothing when it isn't inside text. */
export function docPosToTextOffset(
  projection: PlainTextProjection,
  pos: number,
): Maybe<number> {
  const run = projection.runs[indexAtOrBefore(projection.runs, pos, "docFrom")];
  if (typeof run === "undefined" || pos > run.docFrom + run.length)
    return nothing;
  return just(run.textFrom + (pos - run.docFrom));
}

/**
 * Index of the block holding `offset` (a separator belongs to the block
 * before it), or -1 for an empty projection.
 */
export function blockIndexAt(
  projection: PlainTextProjection,
  offset: number,
): number {
  if (projection.blocks.length === 0) return -1;
  return Math.max(0, indexAtOrBefore(projection.blocks, offset, "textFrom"));
}

/** Text of block `index` (hard breaks kept as `\n`). */
export function blockText(
  projection: PlainTextProjection,
  index: number,
): string {
  const block = projection.blocks[index];
  if (typeof block === "undefined") return "";
  return projection.text.slice(block.textFrom, block.textFrom + block.length);
}
